import { Link } from 'react-router-dom'
import {
  ActionIcon, Alert, Anchor, Badge, Code, CopyButton, Group, Stack, Text, Tooltip,
  Skeleton as MSkeleton,
} from '@mantine/core'
import { IconAlertTriangle, IconCheck, IconCopy, IconServerOff } from '@tabler/icons-react'
import {
  PageTitle, SectionCard, StatTile, StatusBadge, EmptyState, MetaList, HairlineDivider,
} from '@lighthouse/design'
import { CancelRunAction } from '../components/cancel/CancelRunAction'
import { runScopeText } from '../components/cancel/cancelFormat'
import { runOutcomeLook } from '../components/outcomeFormat'
import {
  absenceLabel, activeRunAttachedText, activeRunsView, restartHeadline, slotsLabel, uptimeLabel,
} from '../components/workers/workerFormat'
import { useWorkerFleet } from '../hooks/useOverview'
import { NOT_MEASURED, agoLabel, exactNumber, whenLabel } from '../utils/format'
import { hangfireJobUrl } from '../utils/hangfireUrl'
import type { WorkerActiveRun, WorkerFleet, WorkerNode } from '../types/observatory'

/**
 * Workers — the background-job fleet, per server.
 *
 * `GET api/observatory/workers` has served every figure on this page since the
 * heartbeat projection shipped, and the Overview panel discards eight of them: the
 * status of a LIVE server (so four of the API's five labels had never appeared on
 * screen), the slot counts behind the percentage, a healthy server's last
 * heartbeat, whose uptime the fleet maximum belongs to, `startedAt`, `hostName`,
 * the Hangfire server id as something you can copy rather than hover, and the
 * restart ledger. This page is those fields. It adds no capability and costs no
 * backend work.
 *
 * Four decisions shape it:
 *
 *  1. **A card per server, not a row per server.** The whole point is the eight
 *     discarded fields, and a table wide enough to hold them would truncate every
 *     one of them. A Hangfire fleet here is one to three processes, not a
 *     registry, so the vertical cost is nothing.
 *  2. **The three degraded states are read in a fixed order, and `hasNeverReported`
 *     is read FIRST.** The never-reported response carries `isProjectionStale:
 *     false` and `observedAt: null`, so a page that asks about staleness first
 *     renders an empty projection as healthy and fresh — the exact confident
 *     fiction this surface was rebuilt to remove. Nothing has ever reported, then
 *     the projection itself is stale, then a server has genuinely gone.
 *  3. **A dead server gets no utilization bar.** A bar at 0% reads as "idle and
 *     available", the opposite of what silence means, and it is the reading an
 *     operator would act on wrongly. It gets a stated absence instead. The
 *     Overview panel established this property and it is kept here deliberately.
 *  4. **Nothing is recomputed.** The fleet percentage is total-busy-over-total-slots
 *     rather than the mean of per-server percentages; live workers are counted as
 *     processes while unresponsive ones are counted as machines; a restart is
 *     folded onto the incarnation that replaced it. Each of those is a bug the
 *     server already fixed, with the reasoning recorded beside it, and a
 *     client-side "correction" reintroduces it. This page renders what arrives.
 *
 * Each card also lists the ETL runs that process is performing ("Running now", from
 * `activeRuns`), each with "Cancel run ETL-…" inline (components/cancel). A null
 * `activeRuns` — the execution table does not exist here — is NOT_MEASURED, never an
 * empty list: an unmeasured process must not read as an idle one.
 */
export function WorkersPage() {
  const { data, isLoading, isError } = useWorkerFleet()

  if (isError) {
    return (
      <EmptyState
        title="Could not load the worker fleet"
        message="The worker projection did not respond."
      />
    )
  }

  if (isLoading || !data) {
    return (
      <Stack gap="md">
        <MSkeleton height={140} />
        <MSkeleton height={320} />
      </Stack>
    )
  }

  return (
    <Stack gap="lg" pb="xl">
      <PageTitle subtitle="Every background-job server Automaton has seen, what it is working, and when it last spoke">
        Workers
      </PageTitle>

      {/*
        Branch order is load-bearing, not stylistic. See decision 2 above: the
        never-reported response looks fresh on every other field it carries.
      */}
      {data.hasNeverReported ? (
        <NeverReported />
      ) : (
        <>
          {data.isProjectionStale && <ProjectionStale observedAt={data.observedAt} />}
          <FleetSummary fleet={data} />
          {data.workers.map((worker) => (
            <WorkerCard key={worker.workerId} worker={worker} projectionStale={data.isProjectionStale} />
          ))}
        </>
      )}
    </Stack>
  )
}

/**
 * No worker has ever reported here.
 *
 * Deliberately not the same as a fleet that is down, and the wording says so. A
 * dead fleet has heartbeat rows and this has none, which is the difference between
 * "the workers stopped" and "nothing was ever pointed at this database" — two
 * findings with completely different next steps that a single "no data" would
 * flatten into one.
 */
function NeverReported() {
  return (
    <EmptyState
      icon={<IconServerOff size={28} stroke={1.5} />}
      title="No worker has ever reported to this database"
      message={
        'Automaton publishes a heartbeat every 15 seconds once it is running, and nothing has been '
        + 'recorded here. That is not a fleet that is down — a fleet that is down leaves rows behind.'
      }
    />
  )
}

/**
 * The projection is stale — Automaton stopped publishing.
 *
 * A different failure from a worker going quiet, and it invalidates EVERY figure
 * below including the reassuring ones, so it is stated rather than left to be
 * inferred from a timestamp. The two symptoms need opposite responses: a quiet
 * heartbeat under a fresh observation means a worker stopped; a stale observation
 * means the observer stopped and the workers may be fine.
 */
function ProjectionStale({ observedAt }: { observedAt: string | null }) {
  return (
    <Alert
      icon={<IconAlertTriangle size={16} />}
      color="yellow"
      variant="light"
      title="These figures are history, not status"
    >
      <Text size="sm">
        Automaton last published this projection {agoLabel(observedAt)}, so nothing below is current —
        including every server that reads as healthy. Either the worker host is down, or the heartbeat
        publisher stopped while the workers themselves kept running. Until it resumes, this page reports
        what was true then.
      </Text>
    </Alert>
  )
}

/**
 * The fleet aggregate, exactly as served.
 *
 * `liveWorkerCount` counts processes and `unresponsiveWorkerCount` counts machines.
 * That asymmetry is deliberate and documented at length on the DTO: the heartbeat
 * table doubles as a restart ledger, so counting dead rows as outages reports one
 * developer's three restarts as three missing servers. The two tiles are labelled
 * with their units rather than reconciled.
 *
 * "Restarts folded" is the other half of the same accounting — workers plus folded
 * rows is exactly the number of heartbeat rows the response was built from, so no
 * row disappears from this page without being counted on it.
 */
function FleetSummary({ fleet }: { fleet: WorkerFleet }) {
  const rowsRead = fleet.workers.length + fleet.supersededIncarnationCount

  return (
    <SectionCard
      title="The fleet"
      subtitle={
        fleet.observedAt
          ? `Automaton last published this projection ${whenLabel(fleet.observedAt)}`
          : 'Automaton has never published an observation'
      }
    >
      <div
        className="lh-overview-grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
      >
        {/*
          A percentage over zero live slots is not zero, it is unmeasured — and it
          is unmeasured in exactly the two states this page exists to make
          unmistakable, because the server sums slots over LIVE servers only: a
          fleet that is entirely down, and every stale projection. Printing "0%"
          there reads as "idle, plenty of capacity", which is the inversion the
          cards below each refuse individually. The summary must not undo them.
        */}
        <StatTile
          label="Fleet utilization"
          value={fleet.totalWorkerSlots === 0 ? NOT_MEASURED : `${fleet.utilizationPercent}%`}
          sublabel={
            fleet.totalWorkerSlots === 0
              ? 'No live slots to measure'
              : `${slotsLabel(fleet.busyWorkerSlots, fleet.totalWorkerSlots)}, live servers only`
          }
        />
        <StatTile
          label="Live workers"
          value={exactNumber(fleet.liveWorkerCount)}
          sublabel="processes, not machines"
        />
        {/*
          No status role when the count is zero: a measured zero is reported
          plainly rather than decorated as good. The critical tone is also
          withheld while the projection is stale, because a stale projection
          cannot tell a silent worker from a silent publisher, and the banner
          above has already said so.
        */}
        <StatTile
          label="Not responding"
          value={exactNumber(fleet.unresponsiveWorkerCount)}
          status={fleet.unresponsiveWorkerCount > 0 && !fleet.isProjectionStale ? 'critical' : undefined}
          sublabel="machines with nothing running"
        />
        <StatTile
          label="Restarts folded"
          value={exactNumber(fleet.supersededIncarnationCount)}
          sublabel={`${exactNumber(rowsRead)} heartbeat rows read in total`}
        />
      </div>
    </SectionCard>
  )
}

/**
 * One server.
 *
 * The status badge renders for every server, not only the dead ones — which is the
 * single biggest thing this page adds. The API computes Idle / Working / Near
 * capacity / Saturated and the panel only ever drew the `!isLive` arm, so four of
 * the five labels and three of the four status roles have never been on screen.
 *
 * Restart history is attached per HOST while liveness is per PROCESS, and the fold
 * hangs a host's ledger on exactly one live incarnation. So on a two-process host
 * the restart block appears on one card and not the other. That is correct, and
 * summing it across cards would be double-counting nothing; it is shown where it
 * arrives.
 */
function WorkerCard({ worker, projectionStale }: { worker: WorkerNode; projectionStale: boolean }) {
  const restarts = restartHeadline(worker.supersededIncarnationCount, worker.previousIncarnationLastSeenAt)

  return (
    <SectionCard
      title={worker.name}
      subtitle={
        worker.queues.length > 0
          ? `Dequeues from ${worker.queues.join(' · ')}`
          : 'Reported no queues'
      }
      action={<StatusBadge status={worker.status} label={worker.statusLabel} />}
    >
      <Stack gap="md">
        {worker.isLive && worker.workerCount > 0 ? (
          <div>
            <Group justify="space-between" align="baseline" mb={6}>
              <Text size="sm">{slotsLabel(worker.busyWorkerCount, worker.workerCount)}</Text>
              <Text size="sm" fw={500} style={{ fontVariantNumeric: 'tabular-nums' }}>
                {worker.utilizationPercent}%
              </Text>
            </Group>
            <span className="lh-bar-track">
              <span className="lh-bar-fill" style={{ width: `${worker.utilizationPercent}%` }} />
            </span>
          </div>
        ) : worker.isLive ? (
          /*
            Live, and configured with no slots at all. The server hands back
            status "good" / "Idle" because its utilization arithmetic divides by
            zero to zero — but a process that can accept no work is not idle, it
            is misconfigured, and a 0%-wide bar would say the opposite.
          */
          <Alert color="yellow" variant="light" title="No slots configured">
            <Text size="sm">
              This process is beating but reports zero worker slots, so it can accept no work.
              Check <Code>WorkerCount</Code> on its Hangfire server options.
            </Text>
          </Alert>
        ) : (
          <Alert color="red" variant="light" icon={<IconServerOff size={16} />} title="Not responding">
            <Text size="sm">
              Last heartbeat {agoLabel(worker.lastHeartbeatAt)}.{' '}
              {worker.workerCount > 0
                ? `The ${worker.workerCount} slots configured here are excluded from the fleet totals `
                  + 'above — a silent server is not capacity, and counting it would report a saturated '
                  + 'fleet as comfortably idle.'
                : 'It reported no slots before it went quiet.'}
              {projectionStale
                && ' While the projection is stale this may be the publisher rather than the server.'}
            </Text>
          </Alert>
        )}

        <MetaList
          items={[
            { label: 'Host', value: worker.hostName, mono: true },
            { label: 'Hangfire server id', value: <ServerId value={worker.workerId} /> },
            {
              label: 'Queues',
              value: worker.queues.length > 0 ? worker.queues.join(', ') : 'None reported',
            },
            {
              // Never "0 of 28 busy" on a dead server: the API zeroes the busy
              // count but keeps the configured one, and printing the pair would
              // read as a comfortably idle machine rather than an absent one.
              label: 'Slots',
              value: worker.isLive
                ? `${slotsLabel(worker.busyWorkerCount, worker.workerCount)} (${worker.utilizationPercent}%)`
                : `${worker.workerCount} configured, none counted as fleet capacity`,
            },
            { label: 'Last heartbeat', value: whenLabel(worker.lastHeartbeatAt) },
            { label: 'Started', value: whenLabel(worker.startedAt) },
            { label: 'Uptime', value: uptimeLabel(worker.uptimeSeconds) },
          ]}
        />

        <HairlineDivider />
        <RunningNow worker={worker} />

        {restarts && (
          <>
            <HairlineDivider />
            <Stack gap="xs">
              <Text size="sm" fw={500}>{restarts}</Text>
              <MetaList
                items={[
                  {
                    label: 'Previous incarnation last seen',
                    value: whenLabel(worker.previousIncarnationLastSeenAt),
                  },
                  {
                    label: 'Gap before this one started',
                    value: absenceLabel(worker.startedAt, worker.previousIncarnationLastSeenAt),
                  },
                ]}
              />
              <Text size="xs" c="dimmed">
                Older incarnations of this host, folded onto this row rather than listed as workers. A
                restart gives Hangfire a new server id and the heartbeat table a new row, and the
                publisher keeps them on purpose — the table doubles as a seven-day restart ledger.
              </Text>
            </Stack>
          </>
        )}
      </Stack>
    </SectionCard>
  )
}

/**
 * The ETL runs this process is performing — matched on its Hangfire server id, so a
 * run appears under the incarnation performing it — each with its cancel action.
 *
 * Three states, each said differently: NOT MEASURED (`activeRuns` null: the
 * execution table does not exist here, so nothing is known), performing nothing (a
 * served `[]`), and the runs. A dead incarnation can still list a run, as stale: it
 * died holding it, and cancelling it lets Automaton close it.
 */
function RunningNow({ worker }: { worker: WorkerNode }) {
  const view = activeRunsView(worker)

  return (
    <Stack gap="xs" data-section="running-now">
      <Text size="sm" fw={500}>Running now</Text>
      {view.kind === 'unmeasured' ? (
        <Group gap={6} wrap="nowrap" align="flex-start" data-state="active-runs-unmeasured">
          <Text size="sm" ff="monospace">{NOT_MEASURED}</Text>
          <Text size="sm" c="dimmed">{view.text}</Text>
        </Group>
      ) : view.kind === 'none' ? (
        <Text size="sm" c="dimmed" data-state="active-runs-none">{view.text}</Text>
      ) : (
        view.runs.map((run) => <ActiveRunRow key={`${run.runId}:${run.attachedAt}`} run={run} />)
      )}
    </Stack>
  )
}

function ActiveRunRow({ run }: { run: WorkerActiveRun }) {
  const look = runOutcomeLook(run.runOutcome)
  const jobUrl = run.hangfireJobId ? hangfireJobUrl(run.hangfireJobId) : null

  return (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs" data-active-run={run.runId}>
      <Stack gap={2}>
        <Group gap={6} wrap="wrap">
          <Anchor component={Link} to={`/execution-history/${encodeURIComponent(run.runId)}`} size="sm" ff="monospace">
            {run.runKey}
          </Anchor>
          {look.role
            ? <StatusBadge status={look.role} label={look.label} />
            : <Badge color="gray" variant="light" radius="sm">{look.label}</Badge>}
        </Group>
        <Text size="xs" c="dimmed">
          {activeRunAttachedText(run)} · Hangfire job{' '}
          {run.hangfireJobId == null ? NOT_MEASURED : jobUrl ? (
            <Anchor href={jobUrl} target="_blank" rel="noopener noreferrer" size="xs" ff="monospace">
              {run.hangfireJobId}
            </Anchor>
          ) : (
            <Text span size="xs" ff="monospace">{run.hangfireJobId}</Text>
          )}
          {run.isResume && ' (not the run\'s creating job)'}
        </Text>
      </Stack>
      <CancelRunAction
        compact
        runId={run.runId}
        runKey={run.runKey}
        runOutcome={run.runOutcome}
        scope={runScopeText(run.runKey, null, false)}
      />
    </Group>
  )
}

/**
 * The Hangfire server id, as text that can be selected and copied.
 *
 * It lives in a `title` attribute on the Overview panel, which means it exists only
 * on hover and cannot be selected at all — and this string's entire use is being
 * pasted into the Hangfire dashboard to find the server. Hover is the wrong
 * affordance for something whose only purpose is to be taken somewhere else.
 */
function ServerId({ value }: { value: string }) {
  return (
    <Group gap={6} wrap="nowrap" justify="flex-end">
      <Code style={{ fontSize: 11, overflowWrap: 'anywhere' }}>{value}</Code>
      <CopyButton value={value} timeout={1500}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? 'Copied' : 'Copy the server id'} withArrow>
            <ActionIcon
              variant="subtle"
              size="sm"
              color={copied ? 'teal' : 'gray'}
              onClick={copy}
              aria-label="Copy the Hangfire server id"
            >
              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  )
}
